import { computed, onMounted, watch } from 'vue'
import { useRpQuery, UserReportableError } from '~/composables/graphql'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { graphql } from '~/resources/graphql'
import {
    ValidateKioskDocument,
    type VerifyKioskInput
} from '~/resources/graphql/graphql'
import { useState } from '#app'
import { useI18n } from 'vue-i18n'
import { useLayoutMode } from '~/composables/use-layout-mode'

const STORAGE_KEY_VERIFIED_KIOSK_TOKEN = 'verified-kiosk-token'
const USE_STATE_VERIFIED_KIOSK_TOKEN = 'verified-kiosk-token'

function getKioskToken(storageData: string): string {
    const parts = storageData.split('||RPHQ||')
    return parts.length >= 2 && parts[0].length > 10 ? parts[0] : ''
}

export function useVerifiedKiosk(callbacks?: {
    onSuccess: () => void
    onError: (message: string) => void
    onSettled: () => void
}) {
    const { t } = useI18n({ useScope: 'global' })
    const localStorageData = useState(USE_STATE_VERIFIED_KIOSK_TOKEN, () => '')
    const revalidatedToken = useState(
        'revalidated-kiosk-session-token',
        () => ''
    )

    onMounted(() => {
        if (import.meta.client) {
            localStorageData.value =
                localStorage.getItem(STORAGE_KEY_VERIFIED_KIOSK_TOKEN) ?? ''
        }
    })

    const token = computed(() => {
        return getKioskToken(localStorageData.value)
    })

    const kioskName = computed(() => {
        return localStorageData.value
            .split('||RPHQ||')
            .slice(1)
            .join('||RPHQ||')
    })

    const { isKiosk } = useLayoutMode()
    const quietQuery = useRpQuery({ silent: true })
    const { data: kioskData } = useQuery({
        queryKey: ['VerifiedKiosk', token],
        enabled: computed(
            () => import.meta.client && isKiosk.value && token.value !== ''
        ),
        queryFn: async () => {
            const queriedToken = token.value
            if (revalidatedToken.value !== queriedToken) {
                try {
                    const { validateKiosk } = await quietQuery(
                        ValidateKioskDocument,
                        { input: { token: queriedToken } }
                    )
                    if (
                        validateKiosk.__typename === 'ValidateKioskResult' &&
                        validateKiosk.result
                    ) {
                        revalidatedToken.value = queriedToken
                    }
                } catch {
                    // Session migration must not prevent name refresh or interrupt checkout.
                }
            }

            const data = await quietQuery(
                graphql(/* GraphQL */ `
                    query VerifiedKiosk($token: String!) {
                        kiosk(token: $token) {
                            name
                        }
                    }
                `),
                { token: queriedToken }
            )

            return { token: queriedToken, kiosk: data.kiosk }
        },
        throwOnError: false
    })

    watch(
        kioskData,
        data => {
            if (data === undefined || data.token !== token.value) {
                return
            }

            if (data.kiosk !== null) {
                const storedData =
                    localStorage.getItem(STORAGE_KEY_VERIFIED_KIOSK_TOKEN) ?? ''
                if (getKioskToken(storedData) !== data.token) {
                    localStorageData.value = storedData
                    return
                }

                setKiosk(data.token, data.kiosk.name)
            } else {
                clearToken(data.token)
            }
        },
        { immediate: true }
    )

    const query = useRpQuery()
    const { mutate: verifyCode } = useMutation({
        mutationFn: async (mutationData: VerifyKioskInput) =>
            query(
                graphql(/** @lang GraphQL */ `
                    mutation VerifyKiosk($input: VerifyKioskInput!) {
                        verifyKiosk(input: $input) {
                            __typename
                            ... on VerifyKioskResult {
                                name
                                token
                            }
                            ... on NotFoundProblem {
                                message
                            }
                            ... on RateLimitProblem {
                                message
                            }
                        }
                    }
                `),
                {
                    input: mutationData
                }
            ),
        onSuccess: data => {
            if (data.verifyKiosk.__typename === 'VerifyKioskResult') {
                setKiosk(data.verifyKiosk.token, data.verifyKiosk.name)

                callbacks?.onSuccess()
            } else if (data.verifyKiosk.__typename === 'RateLimitProblem') {
                callbacks?.onError(data.verifyKiosk.message)
            } else {
                callbacks?.onError(t('layout.invalidKioskCode'))
            }
        },
        onError: error => {
            if (!(error instanceof UserReportableError)) {
                callbacks?.onError(t('appError.unexpectedError'))
            }
        },
        onSettled: callbacks?.onSettled
    })

    function setKiosk(verifiedToken: string, name: string) {
        localStorageData.value = verifiedToken + '||RPHQ||' + name
        localStorage.setItem(
            STORAGE_KEY_VERIFIED_KIOSK_TOKEN,
            localStorageData.value
        )
    }

    function clearToken(rejectedToken: string = token.value) {
        if (rejectedToken !== token.value) {
            return
        }

        const storedData =
            localStorage.getItem(STORAGE_KEY_VERIFIED_KIOSK_TOKEN) ?? ''
        // Another tab may have replaced the rejected credential while this tab
        // was still using its old token.
        if (getKioskToken(storedData) !== rejectedToken) {
            localStorageData.value = storedData
            return
        }

        localStorageData.value = ''
        localStorage.removeItem(STORAGE_KEY_VERIFIED_KIOSK_TOKEN)
    }

    return {
        token,
        kioskName,
        verifyCode,
        clearToken
    }
}
